import React, { useState } from 'react';
import { Head, Link, router } from '@inertiajs/react';
import AdminLayout from '../../../components/AdminLayout';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import { ArrowLeft, Edit, Trash2, MapPin, Phone, Mail, Eye, Users, EyeOff, Star } from 'lucide-react';

interface Physiotherapist {
  id: number;
  first_name: string;
  last_name: string;
  gender?: string;
  latitude?: number;
  longitude?: number;
  address?: string;
  city?: string;
  zip?: string;
  phone?: string;
  profile_type?: string;
  profile_score?: number;
  appointment_mode?: string;
  payment_type?: string;
  booking_link?: string;
  profile_complete?: boolean;
  certified?: boolean;
  visible?: boolean;
  approved_specialties?: string[];
  user?: {
    id: number;
    email_address: string;
  };
  created_at: string;
  updated_at: string;
}

interface SpecialtyApproval {
  id: number;
  specialty_name: string;
  approved: boolean;
  approved_at?: string;
  notes?: string;
}

interface Analytics {
  total_views: number;
  unique_visitors: number;
  recent_views: Array<{
    id: number;
    created_at: string;
    ip_address: string;
    user_agent: string;
    referrer?: string;
  }>;
}

interface FilterValue {
  id: number;
  value: string;
  active: boolean;
  position: number;
  physiotherapists_count?: number;
}

interface FilterCategory {
  id: number;
  name: string;
  key: string;
  description: string | null;
  active: boolean;
  position: number;
  filter_values: FilterValue[];
}

interface PhysioFilterValue {
  id: number;
  value: string;
  filter_category: {
    id: number;
    name: string;
    key: string;
  };
}

interface Props {
  physiotherapist: Physiotherapist;
  filter_categories?: FilterCategory[];
  physio_filter_values?: PhysioFilterValue[];
  specialties?: string[];
  specialty_approvals?: { [key: string]: SpecialtyApproval };
  analytics?: Analytics;
}

const ShowPhysiotherapist: React.FC<Props> = ({ 
  physiotherapist, 
  physio_filter_values = [],
  specialties = [],
  specialty_approvals = {},
  analytics
}) => {
  const [approvalNotes, setApprovalNotes] = useState<{ [key: string]: string }>({});

  const handleToggleVisibility = () => {
    router.patch(`/admin/physiotherapists/${physiotherapist.id}/toggle_visibility`, {}, {
      onSuccess: () => {
        router.reload({ only: ['physiotherapist'] });
      }
    });
  };

  const handleToggleSpecialty = async (specialtyName: string, currentlyApproved: boolean) => {
    try {
      if (currentlyApproved) {
        // Unapprove
        const response = await fetch(`/admin/physiotherapists/${physiotherapist.id}/unapprove_specialty`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          },
          body: JSON.stringify({ specialty_name: specialtyName })
        });

        if (response.ok) {
          // Reload the page to refresh data
          router.reload({ only: ['specialty_approvals'] });
        } else {
          alert('Erreur lors de la suppression de l\'approbation');
        }
      } else {
        // Approve
        const response = await fetch(`/admin/physiotherapists/${physiotherapist.id}/approve_specialty`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          },
          body: JSON.stringify({
            specialty_name: specialtyName,
            notes: approvalNotes[specialtyName] || ''
          })
        });

        if (response.ok) {
          // Clear the notes field and reload
          setApprovalNotes(prev => {
            const updated = { ...prev };
            delete updated[specialtyName];
            return updated;
          });
          router.reload({ only: ['specialty_approvals'] });
        } else {
          alert('Erreur lors de l\'approbation');
        }
      }
    } catch (error) {
      console.error('Error toggling specialty:', error);
      alert('Une erreur s\'est produite');
    }
  };
  const getGenderLabel = (gender?: string) => {
    switch (gender) {
      case 'M': return 'Homme';
      case 'F': return 'Femme';
      case 'O': return 'Autre';
      default: return 'Non spécifié';
    }
  };

  // Grouper les filtres par catégorie
  const getFiltersByCategory = () => {
    const filtersByCategory: { [key: string]: PhysioFilterValue[] } = {};
    
    physio_filter_values.forEach(filterValue => {
      const categoryName = filterValue.filter_category.name;
      if (!filtersByCategory[categoryName]) {
        filtersByCategory[categoryName] = [];
      }
      filtersByCategory[categoryName].push(filterValue);
    });
    
    return filtersByCategory;
  };

  const filtersByCategory = getFiltersByCategory();

  return (
    <AdminLayout title={`Kinésithérapeute #${physiotherapist.id}`}>
      <Head title={`${physiotherapist.first_name} ${physiotherapist.last_name} - Admin`} />
      
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={() => window.history.back()}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {physiotherapist.first_name} {physiotherapist.last_name}
              </h1>
              <p className="text-gray-600">Kinésithérapeute #{physiotherapist.id}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button 
              variant={physiotherapist.visible ? "outline" : "default"}
              onClick={handleToggleVisibility}
              className={physiotherapist.visible ? "" : "bg-amber-600 hover:bg-amber-700"}
            >
              {physiotherapist.visible ? (
                <>
                  <EyeOff className="w-4 h-4 mr-2" />
                  Mettre en pause
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Activer
                </>
              )}
            </Button>
            <Link href={`/admin/physiotherapists/${physiotherapist.id}/edit`}>
              <Button variant="outline">
                <Edit className="w-4 h-4 mr-2" />
                Modifier
              </Button>
            </Link>
            <Link href={`/admin/physiotherapists/${physiotherapist.id}/filters`}>
              <Button variant="outline">
                Gérer les filtres
              </Button>
            </Link>
            <Link 
              href={`/admin/physiotherapists/${physiotherapist.id}`} 
              method="delete" 
              as="button"
              onBefore={() => {
                if (!confirm('Êtes-vous sûr de vouloir supprimer ce kinésithérapeute ?')) {
                  return false;
                }
              }}
            >
              <Button variant="outline" className="text-red-600 hover:text-red-800">
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </Button>
            </Link>
          </div>
        </div>

        {/* Physiotherapist Details */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Informations générales */}
          <Card>
            <CardHeader>
              <CardTitle>Informations générales</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">ID</label>
                <p className="text-lg font-semibold text-gray-900">#{physiotherapist.id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Nom complet</label>
                <p className="text-lg font-semibold text-gray-900">
                  {physiotherapist.first_name} {physiotherapist.last_name}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Genre</label>
                <p className="text-lg font-semibold text-gray-900">
                  {getGenderLabel(physiotherapist.gender)}
                </p>
              </div>
              {physiotherapist.phone && (
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-900">{physiotherapist.phone}</span>
                </div>
              )}
              {physiotherapist.user && (
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <Link 
                    href={`/admin/users/${physiotherapist.user.id}`}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    {physiotherapist.user.email_address}
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Adresse */}
          <Card>
            <CardHeader>
              <CardTitle>Adresse</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {physiotherapist.address && (
                <div className="flex items-start space-x-2">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-gray-900">{physiotherapist.address}</p>
                    {physiotherapist.city && (
                      <p className="text-gray-600">
                        {physiotherapist.city}
                        {physiotherapist.zip && `, ${physiotherapist.zip}`}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {(physiotherapist.latitude && physiotherapist.longitude) && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Coordonnées GPS</label>
                  <p className="text-sm text-gray-900">
                    {physiotherapist.latitude}, {physiotherapist.longitude}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Statuts */}
          <Card>
            <CardHeader>
              <CardTitle>Statuts</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  physiotherapist.certified 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {physiotherapist.certified ? 'Certifié' : 'Non certifié'}
                </span>
              </div>
              <div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  physiotherapist.profile_complete 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {physiotherapist.profile_complete ? 'Profil complet' : 'Profil incomplet'}
                </span>
              </div>
              {physiotherapist.profile_score !== undefined && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Score du profil</label>
                  <p className="text-lg font-semibold text-gray-900">{physiotherapist.profile_score}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Filtres du kinésithérapeute */}
        {Object.keys(filtersByCategory).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span>🎯 Filtres sélectionnés</span>
                <span className="text-sm font-normal text-gray-500">
                  ({physio_filter_values.length} filtre(s))
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(filtersByCategory).map(([categoryName, filterValues]) => (
                <div key={categoryName} className="border-b border-gray-100 pb-3 last:border-b-0">
                  <h3 className="text-md font-medium text-gray-900 mb-2">{categoryName}</h3>
                  <div className="flex flex-wrap gap-2">
                    {filterValues.map((filterValue) => (
                      <span 
                        key={filterValue.id} 
                        className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full font-medium"
                      >
                        {filterValue.value}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Analytics */}
        {analytics && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Statistiques de consultation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 text-blue-600 mb-1">
                    <Eye className="w-4 h-4" />
                    <span className="text-sm font-medium">Vues totales</span>
                  </div>
                  <p className="text-3xl font-bold text-blue-900">{analytics.total_views}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 text-green-600 mb-1">
                    <Users className="w-4 h-4" />
                    <span className="text-sm font-medium">Visiteurs uniques</span>
                  </div>
                  <p className="text-3xl font-bold text-green-900">{analytics.unique_visitors}</p>
                </div>
              </div>
              
              {analytics.recent_views.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Dernières visites</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {analytics.recent_views.map((view) => (
                      <div key={view.id} className="text-xs bg-gray-50 p-2 rounded">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">
                            {new Date(view.created_at).toLocaleString('fr-FR')}
                          </span>
                          <span className="text-gray-500 font-mono">{view.ip_address}</span>
                        </div>
                        {view.referrer && (
                          <div className="text-gray-500 mt-1 truncate">
                            Depuis: {view.referrer}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Specialty Approvals */}
        {specialties.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-500" />
                Approbation des spécialités
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Approuvez les spécialités pour lesquelles ce kinésithérapeute est qualifié
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {specialties.map((specialty) => {
                  const approval = specialty_approvals[specialty];
                  const isApproved = approval?.approved || false;
                  
                  return (
                    <div key={specialty} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            id={`specialty-${specialty}`}
                            checked={isApproved}
                            onCheckedChange={() => handleToggleSpecialty(specialty, isApproved)}
                            className="data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                          />
                          <label 
                            htmlFor={`specialty-${specialty}`}
                            className="text-sm font-medium cursor-pointer flex items-center gap-2"
                          >
                            {specialty}
                            {isApproved && (
                              <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                                <Star className="w-3 h-3 mr-1 fill-amber-500" />
                                Spécialiste
                              </Badge>
                            )}
                          </label>
                        </div>
                        {approval?.approved_at && (
                          <span className="text-xs text-gray-500">
                            Approuvé le {new Date(approval.approved_at).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                      </div>
                      
                      {approval?.notes && (
                        <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                          <span className="font-medium">Notes: </span>
                          {approval.notes}
                        </div>
                      )}
                      
                      {!isApproved && (
                        <div>
                          <input
                            type="text"
                            placeholder="Ajouter une note (optionnel)"
                            value={approvalNotes[specialty] || ''}
                            onChange={(e) => setApprovalNotes(prev => ({
                              ...prev,
                              [specialty]: e.target.value
                            }))}
                            className="w-full text-sm border rounded p-2"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Dates */}
        <Card>
          <CardHeader>
            <CardTitle>Dates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">Créé le</label>
              <p className="text-lg font-semibold text-gray-900">
                {new Date(physiotherapist.created_at).toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Modifié le</label>
              <p className="text-lg font-semibold text-gray-900">
                {new Date(physiotherapist.updated_at).toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-4">
              <Link href="/admin/physiotherapists">
                <Button variant="outline">
                  Voir tous les kinésithérapeutes
                </Button>
              </Link>
              <Link href="/admin/physiotherapists/new">
                <Button>
                  Créer un nouveau kinésithérapeute
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default ShowPhysiotherapist; 
